import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { apiBase } from '@/utils/apiBase';
import { Printer } from '@phosphor-icons/react';

const API = apiBase();
const STATUS_LABEL = { present: 'Present', absent: 'Absent', leave: 'Leave', half_day: 'Half Day', holiday: 'Holiday' };
const fmtT = (iso) => iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '-';
const inr = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN')}`;

export default function StaffStatementPage() {
  const { staffId } = useParams();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const token = localStorage.getItem('hc_token') || '';

  useEffect(() => {
    axios.get(`${API}/staff/${staffId}/statement?month=${month}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setData(r.data); setError(''); })
      .catch(err => setError(err?.response?.data?.detail || 'Could not load statement (admin login required)'));
  }, [staffId, month, token]);

  if (error) return <div className="min-h-screen bg-white text-black flex items-center justify-center p-8"><p data-testid="statement-error">{error}</p></div>;
  if (!data) return <div className="min-h-screen bg-white flex items-center justify-center"><div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>;

  const { staff, attendance_summary: att, salary, salary_record, expenses, expense_summary: exps, petty_cash } = data;
  const monthLabel = new Date(`${month}-01`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-white text-black" data-testid="staff-statement-page">
      {/* Screen-only toolbar */}
      <div className="print:hidden sticky top-0 bg-neutral-100 border-b border-neutral-200 px-6 py-3 flex items-center gap-3">
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="statement-month-input"
          className="px-3 py-2 rounded-lg border border-neutral-300 text-sm bg-white" />
        <button onClick={() => window.print()} data-testid="print-statement-btn"
          className="ml-auto px-5 py-2 rounded-full bg-[#FF6B35] text-white text-sm font-semibold flex items-center gap-2">
          <Printer size={16} /> Print
        </button>
      </div>

      <div className="max-w-3xl mx-auto p-8 print:p-4">
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 border-black pb-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold">Staff Monthly Statement</h1>
            <p className="text-sm text-neutral-600 mt-1">HomeCommand · {monthLabel}</p>
          </div>
          <div className="text-right text-sm">
            <p className="font-bold text-lg" data-testid="statement-staff-name">{staff.name}</p>
            <p className="text-neutral-600">{staff.role}{staff.phone ? ` · ${staff.phone}` : ''}</p>
            <p className="text-neutral-600">Joined: {staff.join_date || '-'}</p>
          </div>
        </div>

        {/* Attendance */}
        <Section title="1. Attendance">
          <div className="grid grid-cols-6 gap-2 mb-4 text-center">
            {[['Present', att.present], ['Absent', att.absent], ['Leave', att.leave], ['Half Day', att.half_day], ['Late', att.late_count], ['Hours', att.worked_hours]].map(([l, v]) => (
              <div key={l} className="border border-neutral-300 rounded-lg py-2">
                <p className="text-lg font-bold">{v}</p>
                <p className="text-[10px] text-neutral-500 uppercase">{l}</p>
              </div>
            ))}
          </div>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="border-b border-neutral-300 text-neutral-500">
              <th className="text-left py-1">Date</th><th className="text-left">Status</th><th className="text-left">In</th><th className="text-left">Out</th><th className="text-right">Hours</th><th className="text-right">Late</th>
            </tr></thead>
            <tbody>
              {data.attendance.map(a => (
                <tr key={a.date} className="border-b border-neutral-100">
                  <td className="py-1">{a.date}</td>
                  <td>{STATUS_LABEL[a.status] || a.status}</td>
                  <td>{fmtT(a.check_in)}</td>
                  <td>{fmtT(a.check_out)}</td>
                  <td className="text-right">{a.worked_hours || '-'}</td>
                  <td className="text-right">{a.late_minutes ? `${a.late_minutes}m` : '-'}</td>
                </tr>
              ))}
              {data.attendance.length === 0 && <tr><td colSpan={6} className="py-3 text-center text-neutral-400">No records</td></tr>}
            </tbody>
          </table>
        </Section>

        {/* Salary */}
        <Section title="2. Salary">
          <table className="w-full text-sm">
            <tbody>
              <SRow l="Base salary" v={inr(salary.base_salary)} />
              <SRow l={`Deductions — ${salary.absent_days} absent + ${salary.unpaid_leave_days} unpaid leave (quota ${salary.paid_leave_quota}) + ${salary.half_days} half-day @ ${inr(salary.per_day_rate)}/day`} v={`- ${inr(salary.suggested_deductions)}`} />
              <SRow l={`Petty cash recovery (balance ${inr(salary.petty_cash_balance)})`} v={salary.suggested_advance_recovery > 0 ? `- ${inr(salary.suggested_advance_recovery)}` : '-'} />
              <tr className="border-t-2 border-black font-bold text-base">
                <td className="py-2">Net Payable{salary_record ? ` (recorded: ${salary_record.status})` : ' (suggested)'}</td>
                <td className="py-2 text-right" data-testid="statement-net">{inr(salary_record ? salary_record.net_amount : salary.suggested_net)}</td>
              </tr>
            </tbody>
          </table>
        </Section>

        {/* Expenses & reimbursements */}
        <Section title="3. Expenses Filed & Reimbursements">
          <table className="w-full text-xs border-collapse mb-3">
            <thead><tr className="border-b border-neutral-300 text-neutral-500">
              <th className="text-left py-1">Date</th><th className="text-left">Description</th><th className="text-left">Paid From</th><th className="text-left">Status</th><th className="text-right">Amount</th>
            </tr></thead>
            <tbody>
              {expenses.map((e, i) => (
                <tr key={i} className="border-b border-neutral-100">
                  <td className="py-1">{e.date}</td>
                  <td>{e.description || e.vendor || e.category}</td>
                  <td>{e.paid_from === 'own_pocket' ? `Own${e.settled ? ' (reimbursed)' : ''}` : e.paid_from === 'advance' ? 'Advance' : 'House'}</td>
                  <td className="capitalize">{e.status || 'approved'}</td>
                  <td className="text-right">{inr(e.amount)}</td>
                </tr>
              ))}
              {expenses.length === 0 && <tr><td colSpan={5} className="py-3 text-center text-neutral-400">No expenses filed</td></tr>}
            </tbody>
          </table>
          <div className="text-sm space-y-0.5">
            <p>Approved total: <b>{inr(exps.approved_total)}</b> · Own pocket: <b>{inr(exps.own_pocket_total)}</b></p>
            <p>Reimbursed: <b>{inr(exps.reimbursed)}</b> · <span className={exps.outstanding > 0 ? 'text-red-600 font-bold' : ''}>Outstanding to reimburse: {inr(exps.outstanding)}</span></p>
          </div>
        </Section>

        {/* Petty cash */}
        <Section title="4. Petty Cash (Advance Float)">
          <p className="text-sm mb-2">Current balance: <b data-testid="statement-pc-balance">{inr(petty_cash.balance)}</b> · Total given till date: {inr(petty_cash.total_given)}</p>
          {petty_cash.ledger.length > 0 && (
            <table className="w-full text-xs border-collapse">
              <thead><tr className="border-b border-neutral-300 text-neutral-500">
                <th className="text-left py-1">Date</th><th className="text-left">Type</th><th className="text-left">Note</th><th className="text-right">Amount</th>
              </tr></thead>
              <tbody>
                {petty_cash.ledger.map(l => (
                  <tr key={l.entry_id} className="border-b border-neutral-100">
                    <td className="py-1">{(l.created_at || '').slice(0, 10)}</td>
                    <td className="capitalize">{l.type}</td>
                    <td>{l.note || '-'}</td>
                    <td className="text-right">{l.type === 'deduction' ? '-' : '+'}{inr(Math.abs(l.amount))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        {/* Signatures */}
        <div className="grid grid-cols-2 gap-16 mt-16 text-sm">
          <div className="border-t border-black pt-2">Employer Signature</div>
          <div className="border-t border-black pt-2">Staff Signature ({staff.name})</div>
        </div>
        <p className="text-[10px] text-neutral-400 mt-6">Generated by HomeCommand on {new Date(data.generated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-sm font-bold uppercase tracking-wider border-b border-neutral-300 pb-1 mb-3">{title}</h2>
      {children}
    </div>
  );
}

function SRow({ l, v }) {
  return (
    <tr className="border-b border-neutral-100">
      <td className="py-1.5 text-neutral-700">{l}</td>
      <td className="py-1.5 text-right font-medium whitespace-nowrap">{v}</td>
    </tr>
  );
}
